/* eslint-disable react/no-children-prop */
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import Collapse, { Panel } from "rc-collapse";
import BookingDurationList from "../Duration/BookingDurationList.jsx";
import DurationLabel from "app/pages/.shared/DurationLabel";
import DepartureCityList from "../DepartureCity/DepartureCityList.jsx";
import PassengersCounterDisplay from "app/pages/.shared/PassengersCounterDisplay.jsx";
import Loader from "app/pages/.shared/Loader.jsx";
import Period from "app/pages/.shared/Period.jsx";
import AmountContainer from "app/pages/.shared/AmountContainer.jsx";
import { scrollToElement } from "app/utils/scroller";
import findIndex from "lodash/findIndex";
import find from "lodash/find";
import { FormattedMessage } from "react-intl";
import {
	ALERT_TYPE,
	FS_SEARCH_ENGINE_EVENT_NAME,
	OFFER_TYPES,
	PASSENGER_TYPE,
	RESOLUTION,
	STRATEGY,
} from "app/constants";
import {
	pushTagOnAlternativeDepartureCitySelect,
	pushTagOnDepartureCitySelect,
	pushTagOnDepartureDateSelect,
	pushTagOnDurationSelect,
	pushTagOnOfferTypeSelect,
	pushTagOnPassengerSelect,
} from "app/utils/analytics";
import Button, { BUTTON_SIZES, BUTTON_VARIANTS } from "app/pages/.shared/form/Button";
import PassengersFieldset from "app/pages/PrePackage/Passenger/PassengersFieldset";
import AlternativeDepartureCityList from "app/pages/PrePackage/DepartureCity/AlternativeDepartureCityList";
import get from "lodash/get";
import { SearchEngineOffers } from "app/pages/PrePackage/SearchEngineOffers/SearchEngineOffers";
import { resolutionPropType } from "app/utils/propTypes";
import Modal from "react-modal";
import AuthLayerMobile from "app/pages/Auth/AuthLayerMobile/AuthLayerMobile";
import PayNowNote from "app/pages/.shared/PayNow/PayNowNote";

import "./SearchEngine.scss";
import SearchEngineMessagePlaceholderContainer from "app/pages/PrePackage/SearchEngineMessagePlaceholder/SearchEngineMessagePlaceholderContainer";
import AuthModal from "app/pages/Auth/AuthModal/AuthModal";
import loadable from "@loadable/component";
import SidePanel from "app/pages/.shared/SidePanel/SidePanel";
import SearchEngineStepLabel from "app/pages/PrePackage/components/SearchEngineStepLabel/SearchEngineStepLabel";
import TransportationPictoContainer from "app/pages/.shared/TransportationPicto/TransportationPictoContainer";
import { useLocation } from "react-router-dom";
import { Form, Formik } from "formik";
import AlertMessage from "app/pages/.shared/AlertMessage";
import isEmpty from "lodash/isEmpty";
import { validateSearchEngine } from "app/pages/PrePackage/searchEngineSchema";
import PaymentDescriptionTermsContainer from "app/pages/Booking/Payment/PaymentDescriptionTerms/PaymentDescriptionTermsContainer";
import SearchEngineCreditBannerContainer from "app/pages/PrePackage/components/SearchEngineCreditBanner/SearchEngineCreditBannerContainer";
import { EventCategory, HitType, useFlagship, useFsFlag } from "@flagship.io/react-sdk";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import Paragraphs from "app/pages/.shared/Paragraphs";
import IconInfo from "app/pages/.shared/static/icons/IconInfo";
import FloatingButton from "app/pages/.shared/Popover/FloatingButton";

const DurationDepartureDateSidePanelContent = loadable(() =>
	import(/* webpackChunkName: "duration-departure-date-side-panel-content" */ "app/pages/PrePackage/components/DatePickerCalendar/DurationDepartureDateSidePanelContent")
);

const DatePickerCalendarModal = loadable(() =>
	import(/* webpackChunkName: "date-picker-calendar-modal" */ "app/pages/PrePackage/components/DatePickerCalendar/DatePickerCalendarModal.jsx")
);

const DepartureDatePicker = loadable(() =>
	import(/* webpackChunkName: "datepicker" */ "app/pages/PrePackage/DepartureDate/DepartureDatePicker.jsx")
);

const ACCORDION = {
	OFFERS_PANEL_KEY: "0",
	DEPARTURE_CITIES_PANEL_KEY: "1",
	DURATIONS_DATES_PANEL_KEY: "2",
	PASSENGERS_PANEL_KEY: "3",
};

const mobileModalStyle = {
	overlay: {
		zIndex: 6,
	},
	content: {
		top: "0",
		left: "0",
		right: "0",
		bottom: "0",
		border: "none",
		background: "none",
		overflow: "hidden",
	},
};

const SearchEngine = props => {
	const {
		resolution,
		hasQuoteFullPropertyWarning, // true si on se retrouve sur la page /booking/search avec un quote > FULL_PROPERTY
		prePackageOffers = [],
		allAvailableDurations,
		departureCities = [],
		outreMerDepartureCities = [],
		alternativeDepartureCities = [],
		durations = [],
		isBookingEligibleForDeposit,
		prices = [],
		months = [],
		booking = {},
		mealBasis,
		adultOnly,
		isFlashsale,
		isChildrenAgeRequired,
		productInfo = {},
		shop,
		isDealExpired,
		home = {},
		depositPercentage,
		selectedOfferLabel,
		updateBooking = () => {},
		updateBookingPassager = () => {},
		fetchPrepackagePricesOfDurations = () => {},
		updateBookingChildrenBirthdate = () => {},
		updateBookingInfantsBirthdate = () => {},
		fetchPrepackagePricesOfAlternativeCity = () => {},
		fetchPrepackagePricesOfCity = () => {},
		formInitialValues = {},
		availablePaymentMethods = [],
		protectedPageTexts = {},
		stressMarketingMsg = {},
		warningMessage = false,
		productPriceValue,
	} = props;
	const { sendHits } = useFlagship();

	const showStressMarketingMsgFlag = useFsFlag("stress_marketing_message_display");
	const showStressMarketingMsg =
		showStressMarketingMsgFlag.getValue(false) && !hasQuoteFullPropertyWarning;

	const flag = useFsFlag("marketing_bandeau_facilite_paiement");
	const flagValue = flag.getValue(false);

	const { pathname } = useLocation();

	const isEnglishMarket = shop === "en-GB";

	const isSingleOffer = prePackageOffers.length === 1;
	const selectedOfferTypeValue = booking?.offer?.type;
	const selectedDurationValue = booking?.duration?.value;
	const selectedDepartureCityValue = booking?.departureCity?.code;
	const selectedDepartureDateValue = booking?.departureDate;

	const ctaRef = useRef();
	const offerRef = useRef();
	const durationRef = useRef();
	const departureCityRef = useRef();
	const passengerRef = useRef();

	const [authModalVisible, setAuthModalVisible] = useState(false);
	const [isSidePanelOpen, setIsSidePanelOpen] = useState(false);
	const { stressMarketingLabel } = stressMarketingMsg;

	const getInitialPriceIndex = (prices, currentDepartureDate) => {
		if (!prices || prices.length === 0) {
			return undefined;
		}

		if (currentDepartureDate) {
			return findIndex(prices, price => price.dd === currentDepartureDate);
		}

		const bestPriceIndex = findIndex(prices, price => price.i && price.i.mp === true);

		return bestPriceIndex;
	};

	const [currentPriceIndex, setCurrentPriceIndex] = useState(
		getInitialPriceIndex(prices, booking?.departureDate)
	);

	const [accordionActiveKey, setAccordionActiveKey] = useState(
		selectedOfferTypeValue && selectedDepartureDateValue && selectedDurationValue
			? -1
			: ACCORDION.OFFERS_PANEL_KEY
	);

	const [displayAlternativeCities, setDisplayAlternativeCities] = useState(false);

	const onAdultsSelect = value => {
		updateBookingPassager(PASSENGER_TYPE.ADULT, value);
		pushTagOnPassengerSelect();
	};

	const onInfantsSelect = value => {
		updateBookingPassager(PASSENGER_TYPE.INFANT, value);
		pushTagOnPassengerSelect();
	};

	const onChildrenSelect = value => {
		updateBookingPassager(PASSENGER_TYPE.CHILD, value);
		pushTagOnPassengerSelect();
	};

	const onChildrenAgeChange = (birthdate, index) => {
		updateBookingChildrenBirthdate(birthdate, index);
	};

	const onInfantsAgeChange = (birthdate, index) => {
		updateBookingInfantsBirthdate(birthdate, index);
	};

	const onAlternativeCitiesShow = () => {
		// Pour hacker le bug de la liste des villes alerantives qui est masqué à cause du overflow:hidden de rc-collapse
		// Voir http://stackoverflow.com/questions/23978084/ui-select-inside-of-accordion
		setDisplayAlternativeCities(true);
	};

	const onAlternativeCitiesHide = () => {
		// Pour hacker le bug de la liste des villes alerantives qui est masqué à cause du overflow:hidden de rc-collapse
		// Voir http://stackoverflow.com/questions/23978084/ui-select-inside-of-accordion
		setDisplayAlternativeCities(false);
	};

	const onDurationSelection = durationValue => {
		const { resolution } = props;

		const duration = durations?.find(duration => duration?.value === durationValue);
		updateBooking({ duration });

		if (booking?.offer?.type === OFFER_TYPES.ACCOMODATION_ONLY) {
			fetchPrepackagePricesOfDurations(
				props.uriToFetchForAO,
				duration?.value,
				booking?.offer?.type,
				booking?.offer?.contractType
			);
		}

		const isDesktop = resolution === RESOLUTION.LARGE || resolution === RESOLUTION.XLARGE;

		// repositionner le moteur de rechercher pour afficher le calendrier en entier pour faciliter la saisie
		let offset = location?.pathname === "/booking/search" ? -20 : -75;

		if (isEnglishMarket) {
			offset = 0; // pour que le CTA dans la modal calendar soit visible
		}

		const element = isDesktop ? durationRef.current : offerRef.current;

		scrollToElement({ element, offset });

		pushTagOnDurationSelect(duration?.value);

		if (isEnglishMarket) {
			handleOnPanelOpen();
		}
	};

	const onPriceSelection = (price, stressMessage) => {
		if (stressMessage) {
			sendHits({
				type: HitType.EVENT,
				category: EventCategory.ACTION_TRACKING,
				action: FS_SEARCH_ENGINE_EVENT_NAME.CLICK_ON_PP_SEARCH_CALENDAR_STRESS_HIGH,
			});
		}

		const departureDate = price.dd;
		const endDate = price.ed;

		const selectedIndex = prices?.findIndex(price => price.dd === departureDate);

		setAccordionActiveKey(ACCORDION.PASSENGERS_PANEL_KEY);
		if (selectedIndex !== -1) {
			setCurrentPriceIndex(selectedIndex);
		}
		setTimeout(() => {
			updateBooking({
				departureDate: departureDate,
				endDate: endDate,
				price: price.p,
				publicPrice: price.pp,
				hasSpecialOffer: price.so,
			});
			pushTagOnDepartureDateSelect(departureDate, endDate);

			// TODO ce timeout n'est pas assez long pour que le scroll fonctionne correctement
			setTimeout(() => {
				scrollToElement({ element: ctaRef.current, scrollTo: "bottom", offset: 20 }); // scroll pour afficher tout le moteur de recherche
			}, 20); //need to wait for the collapsable panel to close before scrolling to element*/
		}, 100);
	};

	const changeAccordionActivePanel = activeKey => {
		setAccordionActiveKey(activeKey);
	};

	useEffect(() => {
		let defaultAccordionActiveKey;

		if (!selectedOfferTypeValue && !selectedDurationValue && !selectedDepartureDateValue) {
			defaultAccordionActiveKey = ACCORDION.OFFERS_PANEL_KEY;
		} else if (
			!selectedDepartureCityValue &&
			selectedOfferTypeValue === OFFER_TYPES.FLIGHT_WITH_ACCOMMODATION
		) {
			defaultAccordionActiveKey = ACCORDION.DEPARTURE_CITIES_PANEL_KEY;
		} else if (
			(selectedOfferTypeValue === OFFER_TYPES.FLIGHT_WITH_ACCOMMODATION &&
				(!selectedDurationValue || !selectedDepartureDateValue)) ||
			(selectedOfferTypeValue === OFFER_TYPES.ACCOMODATION_ONLY &&
				(!selectedDurationValue || !selectedDepartureDateValue))
		) {
			defaultAccordionActiveKey = ACCORDION.DURATIONS_DATES_PANEL_KEY;
		} else {
			/* returning here to avoid the accordion to collapse when no criteria are met */
			return;
		}
		setAccordionActiveKey(defaultAccordionActiveKey);
	}, [selectedOfferTypeValue]);

	useEffect(() => {
		// On s'assure d'avoir le bon prix et prix public affiché au dessus du bouton, surtout lorsque l'on change d'offre
		const price = find(prices, { dd: booking.departureDate });
		if (
			price &&
			(booking?.price !== price.p ||
				booking?.publicPrice !== price.pp ||
				booking?.departureDate !== price.dd ||
				booking?.endDate !== price.ed)
		) {
			updateBooking({
				price: price?.p,
				publicPrice: price?.pp,
				departureDate: price?.dd,
				endDate: price?.ed,
				hasSpecialOffer: price?.so,
			});
		} else if (price && price?.ad) {
			// Si la date de départ actuellement enregistré comporte des durées alternatives dans
			// dans l'actuel liste des prix (suite à un changement de durée par exemple),
			// alors on déselectionne et réinitialise le choix de la date de départ
			updateBooking({
				price: undefined,
				publicPrice: undefined,
				departureDate: undefined,
				endDate: undefined,
			});
		}

		// Selection de la date de depart initial après avoir récupérer les prix
		if (!currentPriceIndex && prices && prices.length > 0) {
			setCurrentPriceIndex(getInitialPriceIndex(prices, price && price.dd));
		}
	}, [prices, booking?.price, booking?.publicPrice, booking?.departureDate, booking?.endDate]);

	const closeAuthModal = () => {
		setAuthModalVisible(false);
	};

	useEffect(() => {
		if (authModalVisible) {
			const { resolution } = props;
			const onMobile = resolution === RESOLUTION.SMALL || resolution === RESOLUTION.MEDIUM;
			if (onMobile) {
				window.scrollTo(0, 0);
			}
		}
	}, [authModalVisible]);

	// eslint-disable-next-line consistent-return
	const handleSearchEngineSubmit = (values, actions) => {
		const { handleSubmit, strategy, uuid } = props;

		if (strategy === STRATEGY.PRODUCT_FIRST && !uuid) {
			setAuthModalVisible(true);
			actions.setSubmitting(false); // reenable the cta button
		} else {
			return handleSubmit(values, actions);
		}
	};

	const handleOnPanelClose = () => {
		setIsSidePanelOpen(false);
		setAccordionActiveKey(
			booking?.departureDate
				? ACCORDION.PASSENGERS_PANEL_KEY
				: ACCORDION.DURATIONS_DATES_PANEL_KEY
		);
	};

	const handleOnPanelOpen = () => {
		setIsSidePanelOpen(true);
	};

	const handleOnModalClose = () => {
		setIsSidePanelOpen(false);
	};

	const onMobile = resolution === RESOLUTION.SMALL || resolution === RESOLUTION.MEDIUM;

	const offerHeaderLabel = (
		<SearchEngineStepLabel
			ref={offerRef}
			className="search-engine__header--offer"
			defaultLabel={
				isSingleOffer ? (
					<FormattedMessage id="search.engine.offers.title.single.offer" />
				) : (
					<FormattedMessage id="search.engine.offers.title" />
				)
			}
			isComplete={selectedOfferTypeValue && accordionActiveKey !== ACCORDION.OFFERS_PANEL_KEY}
			label={
				<div className="search-engine__offer-label">
					{selectedOfferLabel}
					{selectedOfferTypeValue === OFFER_TYPES.FLIGHT_WITH_ACCOMMODATION && (
						<TransportationPictoContainer />
					)}
				</div>
			}
		/>
	);

	const departureCityHeaderLabel = (
		<SearchEngineStepLabel
			ref={departureCityRef}
			defaultLabel={
				isSingleOffer ? (
					<FormattedMessage id="search.engine.departure.cities.title.transport.with.verb" />
				) : (
					<FormattedMessage id="search.engine.departure.cities.title.transport" />
				)
			}
			isComplete={
				get(booking, "departureCity.label") &&
				accordionActiveKey !== ACCORDION.DEPARTURE_CITIES_PANEL_KEY
			}
			label={
				<FormattedMessage
					values={{ city: booking.departureCity.label }}
					id="search.engine.departure.city.title.prefix"
				/>
			}
		/>
	);

	const isComplete =
		selectedDurationValue && accordionActiveKey !== ACCORDION.DURATIONS_DATES_PANEL_KEY;
	const durationHeaderLabel = (
		<SearchEngineStepLabel
			onClick={() => {
				if (shop === "en-GB" && selectedDepartureDateValue && selectedDurationValue) {
					handleOnPanelOpen();
				}
			}}
			defaultLabel={<FormattedMessage id="search.engine.durations.dates.title" />}
			isComplete={isComplete}
			ref={durationRef}
			label={
				<Fragment>
					<DurationLabel duration={selectedDurationValue} />

					{selectedDepartureDateValue && (
						<Period
							prefix={<FormattedMessage id="general.period.prefix" />}
							suffix={<FormattedMessage id="general.period.suffix" />}
							fromDate={selectedDepartureDateValue}
							endDate={booking.endDate}
							format={{
								weekday: "short",
								day: "2-digit",
								month: "short",
								year: "numeric",
							}}
							reduce={true}
						/>
					)}
				</Fragment>
			}
			{...stressMarketingLabel &&
				showStressMarketingMsg && {
					stressMarketingLabel: stressMarketingLabel,
				}}
		/>
	);

	const passengersHeaderLabel = (
		<SearchEngineStepLabel
			ref={passengerRef}
			label={
				<PassengersCounterDisplay
					adults={booking.adults}
					infants={booking.infants}
					children={booking.children}
				/>
			}
			defaultLabel={<FormattedMessage id="search.engine.guests.title" />}
			isComplete={
				selectedDepartureDateValue && accordionActiveKey !== ACCORDION.PASSENGERS_PANEL_KEY
			}
		/>
	);

	const searchEngineClassName = classNames({
		"search-engine__single-offer": true,
		"rc-collapse-item--disabled-filter": isDealExpired,
	});

	const offerClassName = classNames({
		"cy-search-engine-offer-panel": true,
		"rc-collapse-item--disabled-filter": isDealExpired,
	});

	const departureCityClassName = classNames({
		"cy-search-engine-departure-city-panel": true, // la lib rc-collapse nous ne permet pas d'utiliser data-testid
		"rc-collapse-item-overflow-visible": displayAlternativeCities,
		"rc-collapse-item-disabled": !selectedOfferTypeValue || isDealExpired,
		"rc-collapse-item-hidden":
			selectedOfferTypeValue === OFFER_TYPES.ACCOMODATION_ONLY || isDealExpired,
	});

	const durationClassName = classNames({
		"cy-search-engine-duration-panel": true, // la lib rc-collapse nous ne permet pas d'utiliser data-testid
		"rc-collapse-item-disabled":
			!selectedDepartureCityValue && selectedOfferTypeValue !== OFFER_TYPES.ACCOMODATION_ONLY,
		"search-engine-duration-panel--with-alert":
			stressMarketingLabel && showStressMarketingMsg && isComplete,
	});

	const passengersClassName = classNames({
		"rc-collapse-item--passengers": true, // la lib rc-collapse nous ne permet pas d'utiliser data-testid
		"cy-search-engine-passenger-panel": true, // la lib rc-collapse nous ne permet pas d'utiliser data-testid
		"rc-collapse-item-disabled":
			booking.offer.type !== OFFER_TYPES.FLIGHT_WITH_ACCOMMODATION
				? !selectedDepartureDateValue
				: !selectedDepartureCityValue || !selectedDepartureDateValue, // attention à ne pas prendre booking.price car le prix est absent pour les départs des villes alternatives
	});

	const shouldHideFooter =
		booking.offer.type !== OFFER_TYPES.FLIGHT_WITH_ACCOMMODATION
			? !selectedDepartureDateValue
			: !selectedDepartureCityValue || !selectedDepartureDateValue;

	const footerClassName = classNames("search-engine__footer", {
		"search-engine__footer--hidden": shouldHideFooter, // attention à ne pas prendre booking.price car le prix est absent pour les départs des villes alternatives
		"search-engine__footer--no-price": !booking.price,
	});

	const isCTADisabled =
		booking?.offer?.type !== OFFER_TYPES.FLIGHT_WITH_ACCOMMODATION
			? !booking?.departureDate
			: !booking?.departureCity?.code || !booking?.departureDate; // attention à ne pas prendre booking.price car le prix est absent pour les départs des villes alternatives

	const payNowNote = booking?.price && (
		<PayNowNote
			total={booking.price}
			depositPercentage={depositPercentage}
			availablePaymentMethods={availablePaymentMethods}
		/>
	);

	const { title: landingTitle, description: landingDescription } = protectedPageTexts;
	const signinMarketingBlock = useMemo(
		() => (
			<div className="auth-modal__marketing">
				<div className="auth-modal__marketing-content">
					<Typography
						variant={TYPOGRAPHY_VARIANTS.XL3}
						variantMD={TYPOGRAPHY_VARIANTS.XL4}
						isBold
					>
						<div className="auth-modal__marketing-title">
							{landingTitle && landingTitle.length > 0 && (
								<Paragraphs paragraphs={landingTitle} enableHTMLFormatting />
							)}
						</div>
					</Typography>

					{landingDescription && landingDescription.length > 0 && (
						<div className="auth-modal__marketing-description">
							<Paragraphs paragraphs={landingDescription} enableHTMLFormatting />
						</div>
					)}
				</div>
			</div>
		),
		[landingTitle, landingDescription]
	);

	const signupMarketingBlock = useMemo(
		() => (
			<div className="auth-modal__marketing">
				<div className="auth-modal__marketing-content">
					<Typography
						variant={TYPOGRAPHY_VARIANTS.XL3}
						variantMD={TYPOGRAPHY_VARIANTS.XL4}
						isBold
					>
						<div className="auth-modal__marketing-title">
							{landingTitle && landingTitle.length > 0 && (
								<Paragraphs paragraphs={landingTitle} enableHTMLFormatting />
							)}
						</div>
					</Typography>

					{landingDescription && landingDescription.length > 0 && (
						<div className="auth-modal__marketing-description">
							<Paragraphs paragraphs={landingDescription} enableHTMLFormatting />
						</div>
					)}
				</div>
			</div>
		),
		[landingTitle, landingDescription]
	);

	return (
		<Fragment>
			<Modal isOpen={authModalVisible && onMobile} style={mobileModalStyle}>
				<AuthLayerMobile
					onAuthSuccess={closeAuthModal}
					signinMarketingBlock={signinMarketingBlock}
					signupMarketingBlock={signupMarketingBlock}
				/>
			</Modal>
			<AuthModal
				home={home}
				isOpen={authModalVisible && !onMobile}
				onAuthSuccess={closeAuthModal}
				signinMarketingBlock={signinMarketingBlock}
				signupMarketingBlock={signupMarketingBlock}
			/>
			<Formik
				enableReinitialize={false}
				initialValues={{
					offerType: "",
					departureCity: "",
					duration: "",
					adults: 2,
					infants: "",
					children: "",
					departureDate: "",
					childrenBirthdates: [],
					infantsBirthdates: [],
					...formInitialValues,
				}}
				validateOnChange={false}
				validateOnBlur={false}
				validate={values => {
					const errors = validateSearchEngine({
						values,
						isChildrenAgeRequired,
						adultOnly,
					});

					// If there is an error about missing birthdate, we have to open the passengers accordion so the user can see the errors
					if (!isEmpty(errors.childrenBirthdates) || !isEmpty(errors.infantsBirthdates)) {
						setAccordionActiveKey(ACCORDION.PASSENGERS_PANEL_KEY);
					}

					return errors;
				}}
				initialStatus={{ success: undefined }}
				onSubmit={handleSearchEngineSubmit}
			>
				{({
					errors,
					status,
					values,
					isSubmitting,
					setFieldValue,
					setFieldError,
					setStatus,
				}) => {
					const onPrePackageOfferTypeSelection = (
						{ type, label, contractType, config, uri },
						negotiated
					) => {
						if (type === OFFER_TYPES.ACCOMODATION_ONLY) {
							updateBooking({
								offer: { type, label, contractType, uri, negotiated },
								config,
								departureCity: {},
								price: undefined,
								publicPrice: undefined,
							});
							if (values?.departureCity) {
								setFieldValue("departureCity", "");
							}
						} else {
							updateBooking({
								offer: { type, label, contractType, negotiated },
								price: undefined,
								publicPrice: undefined,
								config,
							});
						}

						setAccordionActiveKey(
							type === OFFER_TYPES.ACCOMODATION_ONLY
								? ACCORDION.DURATIONS_DATES_PANEL_KEY
								: ACCORDION.DEPARTURE_CITIES_PANEL_KEY
						);
						setTimeout(() => {
							const offset = pathname === "/booking/search" ? -20 : -70;
							scrollToElement({ element: offerRef.current, offset });
							pushTagOnOfferTypeSelect(label);
						}, 100);
					};

					const onMainDepartureCitySelection = selection => {
						const { code, label, uri } = selection || {};
						onDepartureCitySelection(
							{ code, label, uri },
							{ isAlternativeDepartureCity: false }
						);
						pushTagOnDepartureCitySelect(label);
					};

					const onAlternativeDepartureCitySelection = selection => {
						const { code, label, uri } = selection || {};
						onDepartureCitySelection(
							{ code, label, uri },
							{ isAlternativeDepartureCity: true }
						);
						pushTagOnAlternativeDepartureCitySelect(label);
					};

					const onDepartureCitySelection = (selection, option) => {
						const { code, label, uri } = selection || {};
						const { isAlternativeDepartureCity } = option || {};

						setAccordionActiveKey(ACCORDION.DURATIONS_DATES_PANEL_KEY);
						setTimeout(() => {
							updateBooking({
								departureCity: {
									code,
									label,
									isAlternative: isAlternativeDepartureCity,
								},
								duration: {},
								departureDate: undefined,
							});
							setFieldValue("duration", "");
							setFieldValue("departureDate", "");
							const offset = pathname === "/booking/search" ? -20 : -75;

							scrollToElement({ element: offerRef.current, offset }); // scroll pour afficher tout le moteur de recherche

							if (isAlternativeDepartureCity) {
								fetchPrepackagePricesOfAlternativeCity(
									uri,
									code,
									booking?.offer?.type,
									booking?.offer?.contractType
								);
							} else {
								fetchPrepackagePricesOfCity(
									uri,
									code,
									booking?.offer?.type,
									booking?.offer?.contractType
								);
							}
						}, 100);
					};

					const onAlternativeDurationSelection = durationValue => {
						setFieldValue("duration", String(durationValue));

						// reset departure date selection when an alternative duration is selected
						setFieldValue("departureDate", "");

						updateBooking({
							price: undefined,
							publicPrice: undefined,
							departureDate: undefined,
							endDate: undefined,
						});

						onDurationSelection(durationValue);
					};

					let searchEnginePricesNode = false;

					// On verifie que la durée selectionnée est bien dans la liste des durées disponibles
					if (
						prices.length === 0 &&
						selectedDurationValue &&
						find(durations, { value: selectedDurationValue })
					) {
						searchEnginePricesNode = (
							<div className="search-engine__loader">
								<Loader />
							</div>
						);
					} else if (prices.length !== 0) {
						searchEnginePricesNode = (
							<div className="search-engine__price">
								<DepartureDatePicker
									isRental={false}
									productInfo={productInfo}
									offerType={selectedOfferTypeValue}
									initialIndex={currentPriceIndex}
									months={months}
									prices={prices}
									onPriceChange={onPriceSelection}
									selectedDuration={selectedDurationValue}
									onAlternativeDurationChange={onAlternativeDurationSelection}
									showStressMarketingMsg={showStressMarketingMsg}
								/>
							</div>
						);
					}

					let durationDepartureDateNode = (
						<Fragment>
							{durations.length === 0 ? (
								<div className="search-engine__loader">
									<Loader />
								</div>
							) : (
								<div>
									<div className="search-engine__duration">
										<BookingDurationList
											offerType={selectedOfferTypeValue}
											durationValue={selectedDurationValue}
											productInfo={productInfo}
											amountSuffix={
												<FormattedMessage id="general.amount.per.person.suffix.label.short" />
											}
											durations={durations}
											onDurationChange={onDurationSelection}
										/>
									</div>

									{searchEnginePricesNode}
								</div>
							)}
						</Fragment>
					);

					if (shop === "en-GB" && onMobile) {
						durationDepartureDateNode = (
							<Fragment>
								{/*<DatePickerCalendarButton onClick={this.handleOnPanelOpen} />*/}

								{durations.length === 0 ? (
									<div className="search-engine__loader">
										<Loader />
									</div>
								) : (
									<Fragment>
										<div className="search-engine__duration">
											<BookingDurationList
												offerType={selectedOfferTypeValue}
												durationValue={selectedDurationValue}
												productInfo={productInfo}
												amountSuffix={
													<FormattedMessage id="general.amount.per.person.suffix.label.short" />
												}
												durations={durations}
												onDurationChange={onDurationSelection}
												isCollapsable={selectedDepartureDateValue} // si aucune date de depart selectionneée, alors on affiche toutes les durées
											/>
										</div>
									</Fragment>
								)}
							</Fragment>
						);
					} else if (shop === "en-GB" && !onMobile) {
						durationDepartureDateNode = (
							<Fragment>
								{durations.length === 0 ? (
									<div className="search-engine__loader">
										<Loader />
									</div>
								) : (
									<div>
										<div className="search-engine__duration">
											<BookingDurationList
												offerType={selectedOfferTypeValue}
												durationValue={selectedDurationValue}
												productInfo={productInfo}
												amountSuffix={
													<FormattedMessage id="general.amount.per.person.suffix.label.short" />
												}
												durations={durations}
												onDurationChange={onDurationSelection}
												isCollapsable
											/>
										</div>

										{isSidePanelOpen && (
											<DatePickerCalendarModal
												name="departureDate"
												show={isSidePanelOpen}
												initialIndex={currentPriceIndex}
												prices={prices}
												shop={shop}
												isFlashsale={isFlashsale}
												hideSidePanel={handleOnModalClose}
												onPriceChange={onPriceSelection}
												selectedDuration={selectedDurationValue}
												onAlternativeDurationChange={
													onAlternativeDurationSelection
												}
											/>
										)}
									</div>
								)}
							</Fragment>
						);
					}

					return (
						<Form
							id="search-engine"
							className="search-engine"
							onChange={event => {
								if (!isEmpty(errors)) {
									setAccordionActiveKey(ACCORDION.PASSENGERS_PANEL_KEY);
									setFieldError(event.target.name);
								}
								setStatus({});
							}}
						>
							{shop === "en-GB" && onMobile && (
								<SidePanel
									isOpen={isSidePanelOpen}
									onClose={handleOnPanelClose}
									direction={"btt"}
								>
									<DurationDepartureDateSidePanelContent
										offerType={selectedOfferTypeValue}
										durations={durations}
										onDurationChange={onDurationSelection}
										isRental={false}
										productInfo={productInfo}
										initialIndex={currentPriceIndex}
										months={months}
										prices={prices}
										onPriceChange={onPriceSelection}
										selectedDepartureDate={selectedDepartureDateValue}
										selectedDuration={selectedDurationValue}
										onAlternativeDurationChange={onAlternativeDurationSelection}
									/>
								</SidePanel>
							)}

							<Collapse
								accordion={true}
								onChange={changeAccordionActivePanel}
								activeKey={accordionActiveKey}
							>
								{isSingleOffer && !booking.departureCity.label ? (
									<div ref={offerRef} className={searchEngineClassName}>
										<SearchEngineOffers
											prePackageOffers={prePackageOffers}
											allAvailableDurations={
												!hasQuoteFullPropertyWarning &&
												allAvailableDurations
											}
											productInfo={productInfo}
											onOfferChange={onPrePackageOfferTypeSelection}
											productPriceValue={productPriceValue}
										/>
									</div>
								) : (
									<Panel header={offerHeaderLabel} className={offerClassName}>
										<SearchEngineOffers
											prePackageOffers={prePackageOffers}
											allAvailableDurations={
												!hasQuoteFullPropertyWarning &&
												allAvailableDurations
											}
											productInfo={productInfo}
											onOfferChange={onPrePackageOfferTypeSelection}
											productPriceValue={productPriceValue}
										/>
									</Panel>
								)}

								<Panel
									header={departureCityHeaderLabel}
									className={departureCityClassName}
								>
									<div className="search-engine__departure-city">
										{outreMerDepartureCities.length > 0 && (
											<div className="search-engine__subtitle">
												<FormattedMessage id="search.engine.main.departure.cities.title" />
											</div>
										)}
										<DepartureCityList
											productInfo={productInfo}
											departureCities={departureCities}
											showCTA={isSingleOffer}
											onDepartureCityChange={onMainDepartureCitySelection}
										/>

										{outreMerDepartureCities.length > 0 && (
											<div
												className="search-engine__subtitle"
												style={{ marginTop: "14px" }}
											>
												<FormattedMessage id="search.engine.outre.mer.departure.cities.title" />
											</div>
										)}

										{outreMerDepartureCities.length > 0 && (
											<DepartureCityList
												productInfo={productInfo}
												showCTA={isSingleOffer}
												departureCities={outreMerDepartureCities}
												onDepartureCityChange={onMainDepartureCitySelection}
											/>
										)}

										{alternativeDepartureCities.length > 0 && (
											<AlternativeDepartureCityList
												openMenuOnFocus={
													pathname === "/booking/search" ? false : true
												}
												cities={alternativeDepartureCities}
												onDepartureCityChange={
													onAlternativeDepartureCitySelection
												}
												onMenuOpen={onAlternativeCitiesShow}
												onMenuClose={onAlternativeCitiesHide}
												selectedDepartureCityCode={
													selectedDepartureCityValue
												}
											/>
										)}
									</div>
								</Panel>

								{/* hack pour eviter que le collapse affiche le bouton avant le side panel*/}
								{shop === "en-GB" &&
								onMobile &&
								selectedDepartureDateValue &&
								selectedDurationValue ? (
									<div className="rc-collapse-item">
										<div
											className="rc-collapse-header"
											data-testid="cy-search-engine-duration-panel"
										>
											<i className="arrow flip-if-rlt" />
											<div
												className="search-engine-step-label"
												ref={durationRef}
												onClick={handleOnPanelOpen}
											>
												<div className="search-engine__label">
													<Typography
														variant={TYPOGRAPHY_VARIANTS.LARGE}
														isBold
														className="search-engine-step-label__label"
													>
														<DurationLabel
															duration={selectedDurationValue}
														/>

														{selectedDepartureDateValue && (
															<Period
																prefix={
																	<FormattedMessage id="general.period.prefix" />
																}
																suffix={
																	<FormattedMessage id="general.period.suffix" />
																}
																fromDate={
																	selectedDepartureDateValue
																}
																endDate={booking.endDate}
																format={{
																	weekday: "short",
																	day: "2-digit",
																	month: "short",
																	year: "numeric",
																}}
																reduce={true}
															/>
														)}
													</Typography>
												</div>
												<div className="search-engine-step-label__edit">
													<FormattedMessage id="search.engine.edit.label" />
												</div>
											</div>
										</div>
									</div>
								) : (
									<Panel
										header={durationHeaderLabel}
										className={durationClassName}
									>
										{durationDepartureDateNode}
									</Panel>
								)}

								<Panel
									className={passengersClassName}
									header={passengersHeaderLabel}
								>
									<>
										{warningMessage && (
											<AlertMessage
												alertType={ALERT_TYPE.INFO}
												className="warning-usa-destination"
												message={
													<FormattedMessage id="general.warning.adult.destination.usa" />
												}
											/>
										)}
										<PassengersFieldset
											isChildrenAgeRequired={isChildrenAgeRequired}
											adultOnly={adultOnly}
											onAdultsSelect={onAdultsSelect}
											onChildrenSelect={onChildrenSelect}
											onInfantsSelect={onInfantsSelect}
											onChildrenAgeChange={onChildrenAgeChange}
											onInfantsAgeChange={onInfantsAgeChange}
										/>
									</>
								</Panel>
							</Collapse>

							<div className={footerClassName}>
								{booking.price && (
									<div className="search-engine__selected-price">
										<AmountContainer
											amount={booking.price}
											suffix={
												<>
													<FormattedMessage id="general.amount.per.person.suffix.label.short" />

													{booking.price && (
														<FloatingButton
															position="bottom"
															floatingContent={
																<div className="search-engine__note">
																	<FormattedMessage
																		id="search.engine.price.note"
																		values={{
																			meal: mealBasis,
																		}}
																	/>

																	{productInfo.ul === true &&
																		selectedOfferTypeValue ===
																			OFFER_TYPES.FLIGHT_WITH_ACCOMMODATION && (
																			<FormattedMessage id="search.engine.price.note.business">
																				{message => (
																					<div
																						style={{
																							marginLeft: 5,
																							display:
																								"inline-block",
																						}}
																					>
																						{message}
																					</div>
																				)}
																			</FormattedMessage>
																		)}
																</div>
															}
															referenceComponent={
																<IconInfo width={12} height={12} />
															}
														/>
													)}
												</>
											}
										/>
									</div>
								)}

								{!isCTADisabled && onMobile && flagValue && (
									<SearchEngineCreditBannerContainer />
								)}

								{booking.publicPrice &&
									selectedOfferTypeValue !==
										OFFER_TYPES.FLIGHT_WITH_ACCOMMODATION && (
										<div className="search-engine__selected-price search-engine__selected-price--public">
											<AmountContainer
												amount={booking.publicPrice}
												prefix={
													<FormattedMessage id="general.instead.of.price" />
												}
											/>
										</div>
									)}
							</div>
							<div className="search-engine__action-button" ref={ctaRef}>
								{errors?.formGlobalErrors?.length > 0 &&
									errors.formGlobalErrors.map(error => (
										<AlertMessage
											key={error.id}
											message={<FormattedMessage id={error.id} />}
											alertType={ALERT_TYPE.ERROR}
										/>
									))}

								<div className="search-engine__action">
									<Button
										submit
										disabled={isCTADisabled}
										variant={BUTTON_VARIANTS.FILLED}
										size={BUTTON_SIZES.LARGE}
										label={<FormattedMessage id="search.engine.book.now" />}
										loading={isSubmitting}
										data-testid="search-engine-button"
									/>

									{status?.success === false && errors._error && (
										<AlertMessage
											message={<FormattedMessage {...errors._error} />}
											alertType={ALERT_TYPE.ERROR}
										/>
									)}

									{productPriceValue && !shouldHideFooter && (
										<>
											{isBookingEligibleForDeposit ? (
												payNowNote
											) : (
												<PaymentDescriptionTermsContainer />
											)}
										</>
									)}

									<div className="search-engine__marketing-placeholder">
										<SearchEngineMessagePlaceholderContainer />
									</div>
								</div>
							</div>

							{!isCTADisabled && onMobile && !flagValue && (
								<SearchEngineCreditBannerContainer />
							)}
						</Form>
					);
				}}
			</Formik>
		</Fragment>
	);
};

SearchEngine.propTypes = {
	handleSubmit: PropTypes.func,
	uuid: PropTypes.string,
	uriToFetchForAO: PropTypes.string,
	uriToFetchForFA: PropTypes.string,
	allAvailableDurations: PropTypes.arrayOf(PropTypes.number),
	resolution: resolutionPropType,
	strategy: PropTypes.string,
	departureCities: PropTypes.array,
	protectedPageTexts: PropTypes.object,
	outreMerDepartureCities: PropTypes.array,
	alternativeDepartureCities: PropTypes.array,
	durations: PropTypes.array,
	prices: PropTypes.array,
	months: PropTypes.array,
	booking: PropTypes.object,
	adultOnly: PropTypes.bool,
	isChildrenAgeRequired: PropTypes.bool,
	updateBooking: PropTypes.func,
	updateBookingChildrenBirthdate: PropTypes.func,
	updateBookingInfantsBirthdate: PropTypes.func,
	updateBookingPassager: PropTypes.func,
	fetchPrepackagePricesOfDurations: PropTypes.func,
	fetchPrepackagePricesOfAlternativeCity: PropTypes.func,
	fetchPrepackagePricesOfCity: PropTypes.func,
	productInfo: PropTypes.object,
	isFlashsale: PropTypes.bool,
	isDealExpired: PropTypes.bool,
	home: PropTypes.object,
	depositPercentage: PropTypes.number,
	selectedOfferLabel: PropTypes.string,
	shop: PropTypes.string,
	hasQuoteFullPropertyWarning: PropTypes.bool,
	prePackageOffers: PropTypes.array,
	isBookingEligibleForDeposit: PropTypes.bool,
	mealBasis: PropTypes.string,
	formInitialValues: PropTypes.object,
	stressMarketingMsg: PropTypes.object,
	productPriceValue: PropTypes.string,
	availablePaymentMethods: PropTypes.array,
	warningMessage: PropTypes.bool,
};

export default SearchEngine;
